import type { ActiveCustomerProfile } from "~/features/profile/schemas/active-customer-profile-schema.ts";
import type { CustomerDetails } from "~/shared/schemas/customer-schema.ts";

/** Email domain identifying internal TQL staff accounts. */
export const TQL_EMAIL_DOMAIN = "@tql.com";

/**
 * Customer that `@tql.com` users default to on login when it is one of their
 * accessible customers. Overrides the server-persisted active profile.
 */
export const TQL_DEFAULT_CUSTOMER_ID = 215_147;

/**
 * Reads the account's email, preferring `username` and falling back to the
 * `email` id-token claim. Returns a trimmed string (empty when unavailable).
 */
export const getAccountEmail = (
	account:
		| {
				username?: string;
				idTokenClaims?: Record<string, unknown>;
		  }
		| null
		| undefined,
): string => {
	const username = account?.username?.trim() ?? "";
	if (username) {
		return username;
	}

	const emailClaim = account?.idTokenClaims?.["email"];
	return typeof emailClaim === "string" ? emailClaim.trim() : "";
};

type ResolveInitialCustomerArgs = {
	accountEmail: string;
	userCustomers: CustomerDetails[] | undefined;
	activeProfile: ActiveCustomerProfile | undefined;
	activeProfileError: unknown;
};

/**
 * Resolves which customer to select on login.
 *
 * Precedence: `target ?? persisted ?? first`.
 * - `@tql.com` users default to `TQL_DEFAULT_CUSTOMER_ID` when it is in their
 *   accessible list — this overrides the server-persisted active profile.
 * - Otherwise the server-persisted profile is used when accessible.
 * - Failing both, the first accessible customer is used.
 *
 * Returns `undefined` when the user has no accessible customers.
 */
export const resolveInitialCustomer = ({
	accountEmail,
	userCustomers,
	activeProfile,
	activeProfileError,
}: ResolveInitialCustomerArgs): CustomerDetails | undefined => {
	if (!userCustomers?.length) {
		return undefined;
	}

	const isTqlUser = accountEmail.toLowerCase().endsWith(TQL_EMAIL_DOMAIN);
	const targetCustomer = isTqlUser
		? userCustomers.find((customer) => customer.id === TQL_DEFAULT_CUSTOMER_ID)
		: undefined;

	const persistedCustomer =
		!activeProfileError && activeProfile
			? userCustomers.find(
					(customer) => customer.id === activeProfile.customerProfileId,
				)
			: undefined;

	return targetCustomer ?? persistedCustomer ?? userCustomers[0];
};
